// RewardContext — provides live coins, EXP, and level state to the entire app.
// Uses Firestore onSnapshot so the UI updates instantly when rewards are written.
// Works for both students (userId = auth UID) and children (uses childId from localStorage).

import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { db } from '@/firebase/config'
import { useAuth } from '@/context/AuthContext'
import { getLevelProgress, getExpToNextLevel, getLevelFromExp } from '@/utils/rewardUtils'
import { MISSION_CONTEXT_CHANGED_EVENT } from '@/utils/missionUtils'
import {
  isSchoolStudentAccount,
  clearChildSessionKeys,
  normalizeOwnedItemMap,
  pickRewardTotals
} from '@/utils/rewardDocUtils'
import { ensureStudentRewardDocFromUserProfile } from '@/services/rewardService'

const RewardContext = createContext(null)

function applyRewardSnapshot(data, setters) {
  const { totalCoins, totalExp } = pickRewardTotals(data)
  const levelObj = getLevelFromExp(totalExp)
  const { ownedItems, ownedItemIds } = normalizeOwnedItemMap(data?.ownedItemIds)

  setters.setTotalCoins(totalCoins)
  setters.setTotalExp(totalExp)
  setters.setCurrentLevel(levelObj.level)
  setters.setLevelLabel(data?.levelLabel || levelObj.label)
  setters.setExpToNextLevel(getExpToNextLevel(totalExp))
  setters.setLevelProgress(getLevelProgress(totalExp))
  setters.setOwnedItems(ownedItems)
  setters.setOwnedItemIds(ownedItemIds)
  setters.setEquippedAvatarItemId(data?.equippedAvatarItemId ?? null)
}

function applyEmptyRewardState(setters) {
  setters.setTotalCoins(0)
  setters.setTotalExp(0)
  setters.setCurrentLevel(1)
  setters.setLevelLabel('Rookie')
  setters.setExpToNextLevel(200)
  setters.setLevelProgress(0)
  setters.setOwnedItemIds(new Set())
  setters.setOwnedItems({})
  setters.setEquippedAvatarItemId(null)
}

export function RewardProvider({ children }) {
  const { currentUser, userRole } = useAuth()

  const [totalCoins, setTotalCoins] = useState(0)
  const [totalExp, setTotalExp] = useState(0)
  const [currentLevel, setCurrentLevel] = useState(1)
  const [levelLabel, setLevelLabel] = useState('Rookie')
  const [expToNextLevel, setExpToNextLevel] = useState(200)
  const [levelProgress, setLevelProgress] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  const [ownedItemIds, setOwnedItemIds] = useState(() => new Set())
  const [ownedItems, setOwnedItems] = useState(() => ({}))
  const [equippedAvatarItemId, setEquippedAvatarItemId] = useState(null)

  const [contextVersion, setContextVersion] = useState(0)

  const setters = {
    setTotalCoins,
    setTotalExp,
    setCurrentLevel,
    setLevelLabel,
    setExpToNextLevel,
    setLevelProgress,
    setOwnedItemIds,
    setOwnedItems,
    setEquippedAvatarItemId
  }

  useEffect(() => {
    const handler = () => setContextVersion((v) => v + 1)
    window.addEventListener(MISSION_CONTEXT_CHANGED_EVENT, handler)
    return () =>
      window.removeEventListener(MISSION_CONTEXT_CHANGED_EVENT, handler)
  }, [])

  const getDocId = useCallback(() => {
    if (!currentUser?.uid) return null

    if (isSchoolStudentAccount(userRole, currentUser)) {
      return currentUser.uid
    }

    const contextRaw = localStorage.getItem('currentMissionContext')
    if (contextRaw) {
      try {
        const ctx = JSON.parse(contextRaw)
        if (
          ctx.userType === 'child' &&
          ctx.userId &&
          ctx.parentUserId === currentUser.uid
        ) {
          return `${currentUser.uid}_child_${ctx.userId}`
        }
      } catch {
        // malformed context — fall through to direct uid
      }
    }
    return currentUser.uid
  }, [currentUser?.uid, currentUser?.role, userRole])

  useEffect(() => {
    if (!currentUser?.uid) {
      setIsLoading(false)
      setLoadError(null)
      return
    }

    const isStudent = isSchoolStudentAccount(userRole, currentUser)
    if (isStudent) {
      clearChildSessionKeys()
    }

    const docId = getDocId()
    if (!docId) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setLoadError(null)

    let cancelled = false
    const rewardRef = doc(db, 'userRewards', docId)

    const mergeUserProfileFallback = async (rewardData) => {
      if (!isStudent || cancelled) return rewardData

      const rewardTotals = pickRewardTotals(rewardData)
      if (rewardTotals.totalCoins > 0 || rewardTotals.totalExp > 0) {
        return rewardData
      }

      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid))
        if (!userSnap.exists()) return rewardData

        const profileTotals = pickRewardTotals(userSnap.data())
        if (profileTotals.totalCoins === 0 && profileTotals.totalExp === 0) {
          return rewardData
        }

        return {
          ...(rewardData || {}),
          totalCoins: profileTotals.totalCoins,
          totalExp: profileTotals.totalExp,
          currentLevel:
            rewardData?.currentLevel ?? userSnap.data()?.currentLevel,
          levelLabel: rewardData?.levelLabel ?? userSnap.data()?.levelLabel
        }
      } catch (err) {
        console.warn('RewardContext: users profile fallback failed', err)
        return rewardData
      }
    }

    const handleSnapshot = async (snap) => {
      if (cancelled) return

      if (snap.exists()) {
        const merged = await mergeUserProfileFallback(snap.data())
        applyRewardSnapshot(merged, setters)
        setLoadError(null)
        setIsLoading(false)
        return
      }

      if (isStudent) {
        const created = await ensureStudentRewardDocFromUserProfile(
          currentUser.uid
        )
        if (created) {
          return
        }
      }

      const merged = await mergeUserProfileFallback(null)
      if (merged && (merged.totalCoins > 0 || merged.totalExp > 0)) {
        applyRewardSnapshot(merged, setters)
      } else {
        applyEmptyRewardState(setters)
      }
      setIsLoading(false)
    }

    const unsubscribe = onSnapshot(
      rewardRef,
      (snap) => {
        handleSnapshot(snap).catch((err) => {
          console.warn('RewardContext: snapshot handler failed', err)
          if (!cancelled) setIsLoading(false)
        })
      },
      (err) => {
        console.warn('RewardContext: onSnapshot error', {
          docId,
          code: err?.code,
          message: err?.message
        })
        if (cancelled) return

        mergeUserProfileFallback(null)
          .then((merged) => {
            if (merged && (merged.totalCoins > 0 || merged.totalExp > 0)) {
              applyRewardSnapshot(merged, setters)
              setLoadError(null)
            } else {
              applyEmptyRewardState(setters)
              setLoadError(err?.code || 'reward_load_failed')
            }
          })
          .finally(() => {
            if (!cancelled) setIsLoading(false)
          })
      }
    )

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [currentUser?.uid, currentUser?.role, userRole, getDocId, contextVersion])

  const value = {
    totalCoins,
    totalExp,
    currentLevel,
    levelLabel,
    expToNextLevel,
    levelProgress,
    isLoading,
    loadError,
    ownedItemIds,
    ownedItems,
    equippedAvatarItemId
  }

  return (
    <RewardContext.Provider value={value}>
      {children}
    </RewardContext.Provider>
  )
}

export function useReward() {
  const ctx = useContext(RewardContext)
  if (!ctx) throw new Error('useReward must be used inside RewardProvider')
  return ctx
}
